import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trash2, RotateCcw, Archive, HardDrive, FileX, RefreshCw, FileText, User, Eye } from 'lucide-react';
import { deletedDocumentAPI } from '../services/api';
import { useToast, useConfirm } from './UIProvider';
import { formatClinicDateTime } from '../utils/clinicTime';
import SortHeader, { useSort, sortRows } from './SortHeader';
import TablePager, { usePagedRows } from './TablePager';

// Admin-only review queue for documents that staff (non-admin) soft-deleted. Admins Restore, Keep
// (retain, leaves the queue), or Permanently delete each — plus a disk-cleanup tool that purges all
// pending deleted documents and reports the space freed.

const formatBytes = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

// Per-tab summary + cleanup wording. Each tab owns its own scope so nothing reads as a confusing
// global number.
const TAB_META = {
  pending: {
    label: 'Reclaimable disk space',
    sublabel: 'pending review',
    cleanupLabel: 'Clean up disk',
    cleanupTip: 'Permanently delete all pending documents',
    noun: 'pending',
  },
  kept: {
    label: 'Kept (retained) documents',
    sublabel: 'archived',
    cleanupLabel: 'Delete all kept',
    cleanupTip: 'Permanently delete all kept documents',
    noun: 'kept',
  },
  all: {
    label: 'All deleted documents',
    sublabel: '',
    cleanupLabel: 'Delete all',
    cleanupTip: 'Permanently delete all deleted documents',
    noun: 'deleted',
  },
};

const DeletedDocumentsManagement = () => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [pending, setPending] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [filter, setFilter] = useState('pending'); // pending | kept | all

  // View opens the document inline in a new tab. Soft-deleted docs are served by an admin-only
  // endpoint that authenticates via a ?token= query param (a new tab can't send an auth header).
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  const authToken = localStorage.getItem('clinic_auth_token');
  const viewUrl = (id) => `${apiBase}/admin/deleted-documents/${id}/view?token=${authToken}`;

  const load = useCallback(async () => {
    try {
      const list = await deletedDocumentAPI.getDeletions(filter);
      setPending(Array.isArray(list) ? list : []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load deleted documents');
      setPending([]);
    }
  }, [filter]);

  // Summary is derived from the current tab's loaded rows, so it's always local to the tab.
  const docs = pending || [];
  const totalCount = docs.length;
  const totalBytes = docs.reduce((s, d) => s + (d.fileSize || 0), 0);
  const meta = TAB_META[filter];

  useEffect(() => { load(); }, [load]);

  const act = async (fn, d, successMsg) => {
    setBusyId(d.id);
    try {
      await fn(d.id);
      showToast('success', successMsg);
      await load();
      window.dispatchEvent(new Event('clinic:deleted-docs-changed'));
    } catch (e) {
      showToast('error', e.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const docLabel = (d) => d.originalFileName || d.fileName || 'document';

  // Size sorts on the raw byte count, not on the "1.2 MB" the cell prints: as text, 900 KB would
  // outrank 1.2 MB.
  const { sortConfig, handleSort } = useSort();
  const sortedPending = useMemo(() => sortRows(docs, sortConfig, {
    document:  (d) => d.originalFileName || d.fileName,
    patient:   (d) => d.patientName,
    deletedBy: (d) => d.deletedByName,
    size:      (d) => d.fileSize || 0,
  }), [docs, sortConfig]);
  const paging = usePagedRows(sortedPending, { id: 'deleted-documents', resetOn: [filter, sortConfig] });

  const handleRestore = (d) => act(deletedDocumentAPI.restore, d, `Restored "${docLabel(d)}"`);
  const handleKeep = (d) => act(deletedDocumentAPI.keep, d, `Kept "${docLabel(d)}" (archived)`);
  const handlePurge = async (d) => {
    const ok = await confirm({
      title: 'Permanently delete?',
      subtitle: 'This cannot be undone',
      message: `Permanently remove "${docLabel(d)}" from the server's disk?`,
      confirmText: 'Delete permanently',
      tone: 'danger',
    });
    if (!ok) return;
    act(deletedDocumentAPI.purge, d, 'Document permanently deleted');
  };

  const handleCleanup = async () => {
    if (!totalCount) return;
    const ok = await confirm({
      title: `${meta.cleanupLabel}?`,
      subtitle: 'This cannot be undone',
      message: `Permanently delete all ${totalCount} ${meta.noun} document${totalCount === 1 ? '' : 's'} and free up ~${formatBytes(totalBytes)} of disk space?`,
      confirmText: meta.cleanupLabel,
      tone: 'danger',
    });
    if (!ok) return;
    setCleaning(true);
    try {
      const r = await deletedDocumentAPI.cleanup(filter);
      showToast('success', `Freed ${r.freedDisplay || ''} - removed ${r.purgedCount} document${r.purgedCount === 1 ? '' : 's'}`);
      await load();
      window.dispatchEvent(new Event('clinic:deleted-docs-changed'));
    } catch (e) {
      showToast('error', e.message || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <FileX className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Deleted Documents</h1>
                <p className="text-gray-600">Review documents deleted by staff - restore, keep, or permanently delete</p>
              </div>
            </div>
            <button
              onClick={load}
              className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50"
              title="Refresh"
            >
              <RefreshCw className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg text-red-700">{error}</div>
        )}

        {/* Filter tabs — Kept / All keep archived documents accessible for later restore or delete. */}
        <div className="flex gap-2 mb-4">
          {[
            { key: 'pending', label: 'Pending review' },
            { key: 'kept', label: 'Kept' },
            { key: 'all', label: 'All' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                filter === t.key
                  ? 'bg-navy-800 text-white border-navy-800'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Per-tab summary + scoped cleanup — local to the active tab. */}
        {totalCount > 0 && (
          <div className="bg-white rounded-2xl shadow-md p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
                <HardDrive className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  {meta.label}
                  {meta.sublabel && <span className="text-gray-400"> ({meta.sublabel})</span>}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatBytes(totalBytes)}
                  <span className="text-sm font-medium text-gray-500 ml-2">
                    across {totalCount} document{totalCount === 1 ? '' : 's'}
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={handleCleanup}
              disabled={cleaning}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={meta.cleanupTip}
            >
              <Trash2 className="h-4 w-4" />
              <span>{cleaning ? 'Cleaning up…' : meta.cleanupLabel}</span>
            </button>
          </div>
        )}

        {/* Document queue */}
        {pending === null ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
          </div>
        ) : pending.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <Archive className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-500">
              {filter === 'kept' ? 'No kept documents' : filter === 'all' ? 'No deleted documents' : 'Nothing to review'}
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              {filter === 'kept'
                ? 'Documents you choose to keep (archive) will appear here.'
                : filter === 'all'
                ? 'Deleted documents will appear here.'
                : 'Documents deleted by staff will appear here for your review.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <TablePager paging={paging} noun="document" />
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <SortHeader label="Document" sortKey="document" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                  <SortHeader label="Patient" sortKey="patient" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                  <SortHeader label="Deleted by" sortKey="deletedBy" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                  <SortHeader label="Size" sortKey="size" sortConfig={sortConfig} onSort={handleSort} align="right" className="px-6 py-3" />
                  <th className="text-right px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paging.pageRows.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50/60">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate max-w-xs">{docLabel(d)}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            <span>{d.documentType || '—'}</span>
                            {filter !== 'pending' && (
                              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${d.reviewed ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'}`}>
                                {d.reviewed ? 'Kept' : 'Pending'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-sm text-gray-800 flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-gray-400" />{d.patientName || '—'}
                      </div>
                      {d.patientMrn && <div className="text-xs text-blue-600 font-mono">{d.patientMrn}</div>}
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-sm text-gray-800">{d.deletedByName || '—'}</div>
                      {d.deletedAt && (
                        <div className="text-xs text-gray-400">{formatClinicDateTime(d.deletedAt)}</div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right text-sm text-gray-700 whitespace-nowrap">{d.fileSizeDisplay || '—'}</td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <a
                          href={viewUrl(d.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                          title="View document"
                        >
                          <Eye className="h-3.5 w-3.5" /> View
                        </a>
                        <button
                          onClick={() => handleRestore(d)}
                          disabled={busyId === d.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50"
                          title="Restore - make the document visible again"
                        >
                          <RotateCcw className="h-3.5 w-3.5" /> Restore
                        </button>
                        {!d.reviewed && (
                          <button
                            onClick={() => handleKeep(d)}
                            disabled={busyId === d.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                            title="Keep - retain (archived) and remove from the pending queue"
                          >
                            <Archive className="h-3.5 w-3.5" /> Keep
                          </button>
                        )}
                        <button
                          onClick={() => handlePurge(d)}
                          disabled={busyId === d.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                          title="Permanently delete from disk"
                        >
                          {busyId === d.id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <TablePager paging={paging} noun="document" position="bottom" />
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletedDocumentsManagement;
